import React, { useState, useMemo } from 'react';
import { List, Checkbox, Icon, SearchBox, mergeStyleSets, keyframes } from '@fluentui/react';
import classes from 'classnames';

import { includesParttern, mergeLists, MergeListsAction } from 'shared/utils/utils';


const rotation = keyframes({
  from: {
    transform: 'rotate(0deg)',
  },
  to: {
    transform: 'rotate(359deg)'
  }
});


const classNames = mergeStyleSets({
  root: {
    border: '1px solid #A6A6A6',
  },
  fieldsSearch: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: '1px solid #A6A6A6',
    selectors: {
      '& > :first-child': {
        marginLeft: 8,
      },
      '& > :last-child': {
        flexGrow: 1,
        border: 0,
      },
    },
  },
  treeList: {
    margin: '8px 0px 0px 0px',
    maxHeight: '40vh',
    minHeight: 24,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  fieldsListItem: {
    display: 'flex',
    width: '100%',
    padding: '2px 8px',
    cursor: 'pointer',
    alignItems: 'baseline',
    selectors: {
      '&:hover': {
        backgroundColor: 'lightgrey',
      },
    },
  },
  loadingAnimation: {
    animation: `${rotation} 2s infinite linear`,
  },
});


const ID_SEPARATOR = '.';


export const makeId = (index, parentId = '') => `${parentId}${!!parentId ? ID_SEPARATOR : ''}${index}`;
export const idToIndexes = (id) => id.split(ID_SEPARATOR);

export function getItem(items, id) {
  const indexes = idToIndexes(id);
  return indexes.reduce((out, x, index) => index === indexes.length - 1 ? out[x] : out[x].descendants, items);
}

export function getItems(allItems, idsToGet) {
  return idsToGet.map(id => getItem(allItems, id));
}

export function updateItems(items, updatedItems) {
  return updatedItems.map(item => Object.assign(getItem(items, item.id), item));
}


export const IconName = {
  Expanded: 'ChevronDownMed',
  Collapsed: 'ChevronRightMed',
  Loading: 'Refresh',
};

export function TreeSearch(props) {
  const {
    placeholder = 'Start Typing to Filter',
    showCollapseButton = true, 
    areAllCollapsed, onAllCollapsed, areAllSelected, onAllSelected, onFilterChanged, disabled
  } = props;

  return (
    <div className={classNames.fieldsSearch}>
      { showCollapseButton &&
        <Icon
          style={{ marginRight: '4px' }}
          iconName={areAllCollapsed ? IconName.Collapsed : IconName.Expanded}
          onClick={() => onAllCollapsed && onAllCollapsed(!areAllCollapsed)}
        />
      }
      <Checkbox
        checked={areAllSelected}
        onChange={(_, checked) => onAllSelected && onAllSelected(checked) }
        disabled={disabled}
      />
      <SearchBox
        placeholder={placeholder}
        onChange={event => onFilterChanged(event?.target?.value ?? '') }
        onClear={() => onFilterChanged('') }
        iconProps={{ iconName: 'Filter' }}
        disabled={disabled}
      />
    </div>
  );
}


function TreeList(props) {
  const {
    items = [],
    showCollapseButtons = true,
    onItemCollapsed,
    onItemClicked,
    onRenderItem,
    onRenderItemTitle,
    onShouldVirtualize,
    className,
    disabled,
  } = props;

  // console.log('TreeList::props: ', props);

  return (
    <List
      className={`${className}`}
      disabled={disabled}
      onShouldVirtualize={onShouldVirtualize}
      onRenderCell={item=>
        <TreeItem
          key={item.id}
          showCollapseButtons={showCollapseButtons}
          onCollapsed={onItemCollapsed}
          onClicked={onItemClicked}
          onRender={onRenderItem}
          onRenderTitle={onRenderItemTitle}
          item={item}
          disabled={disabled}
        /> }
      items={items}
    />
  );
}

export default TreeList;

function TreeItem(props) {
  const {
    showCollapseButtons = true,
    onCollapsed,
    onClicked,
    onRender,
    onRenderTitle,
    item,
    disabled,
  } = props;

  const {
    text = '',
    isVisible = true,
    canHaveDescendants = false,
    descendants = [],
    isLoading = false,
    isCollapsed = true,
  } = item;

  const nestingDepth = item.nestingDepth ?? props.nestingDepth ?? 0;

  if (!!onRender) {
    return onRender(item, nestingDepth);
  }

  if (!isVisible) {
    return null;
  }

  const iconName = isLoading
    ? IconName.Loading
    : isCollapsed ? IconName.Collapsed : IconName.Expanded;
  const displayCollapseButton = showCollapseButtons && canHaveDescendants;

  return (
    <>
      <div key={item.id} className={classNames.fieldsListItem} onClick={(e) => onClicked && onClicked(item) }
      >
        <span style={{ marginLeft: nestingDepth * 20 + (displayCollapseButton ? 0 : 18)}}></span>
        { displayCollapseButton && 
          <Icon
            className={classes({ [classNames.loadingAnimation] : isLoading })} 
            style={{ marginRight: '4px' }} 
            iconName={iconName} 
            onClick={(e) => {
              e.stopPropagation();
              onCollapsed(!isCollapsed, item)
            }} 
          />}
        
        {!!onRenderTitle
          ? onRenderTitle(item, nestingDepth)
          : <span>{text}</span>
        }
      </div>
      { !isCollapsed && descendants.map(child => 
        <TreeItem 
          key={child.id} showCollapseButtons={showCollapseButtons} 
          onClicked={onClicked} onCollapsed={onCollapsed}
          onRender={onRender} onRenderTitle={onRenderTitle}
          item={child} nestingDepth={nestingDepth + 1}
          disabled={disabled}
        />) }
    </>
  );
}


export function useTreeListFiltering(props) {
  const {
    items = [],
    separateCollapseForFilters = true,
    selectedIds = [],
    expandedIds = [],
    filter = '',
    limitSelectionByNumber = Number.POSITIVE_INFINITY,
    onItemsSelected,
    onItemsCollapsed,
    onFilterChanged,
    onFilterApplied,
  } = props;

  
  // --- State ---
  // const [filter, setFilter] = useState('');
  const [filteredCollapsedIds, setFilteredCollapsedIds] = useState([]);
  const useFilteredCollapsedIds = separateCollapseForFilters && !!filter;


  // --- Memo ---
  const commonDeps = [items, separateCollapseForFilters, filter];

  // TODO: figure out how to use filtered items together with updateItems() 
  // when ids do not match indexes in nested arrays. Then use flatItems() or cleanItems()
  // TODO: do we gain any benefit from that?
  
  // const flatItems = (items) => {
  //   return items.reduce((out, x) => {
  //     if (x.isVisible) {
  //       out.push(x);
  //       x.descendants && !x.isCollapsed && out.push(...flatItems(x.descendants));
  //     }
  //     return out;
  //   }, []);
  // }

  // const cleanItems = (items) => {
  //   return items.reduce((out, x) => {
  //     if (x.isVisible) {
  //       x.descendants = x.isCollapsed ? [] : cleanItems(x.descendants);
  //       out.push(x);
  //     }
  //     return out;
  //   }, []);
  // }

  const displayedItems = useMemo(() => {
    // console.log('useTreeListFiltering:: Filter items: ', filteranimation, items);
    const { items: filteredItems } = filterItems(items, filter);

    // console.log('useTreeListFiltering:: Rerended displayed: ', filteredCollapsedItems, useFilteredCollapsedItems);

    updateItems(filteredItems, selectedIds.map(id => ({ id, isSelected: true })));
    updateItems(filteredItems, useFilteredCollapsedIds // Keep in sync with filterItems
      ? filteredCollapsedIds.map(id => ({ id, isCollapsed: true }))
      : expandedIds.map(id => ({ id, isCollapsed: false })));

    
    // console.log('useTreeListFiltering:: Filtered items ', filteredItems);
    return filteredItems;
  }, [...commonDeps, selectedIds, expandedIds, filteredCollapsedIds, limitSelectionByNumber]);

  const areAllCollapsed = useMemo(() => {
    function areCollapsed(items) {
      return items.every(x => (!x.isVisible || x.isCollapsed || !x.descendants.length) && areCollapsed(x.descendants));
    }

    return areCollapsed(displayedItems);
  }, [...commonDeps, expandedIds, filteredCollapsedIds]);

  const areAllSelected = useMemo(() => {
    function areSelected(items) {
      return items.every(x => (!x.isVisible || !x.isActive || !x.isSelectable || x.isSelected) && areSelected(x.descendants));
    }

    return areSelected(displayedItems);
  }, [...commonDeps, selectedIds, limitSelectionByNumber]);


  // --- Collapse ---
  function handleItemCollapsed(isCollapsed, item) {
    // console.log('useTreeListFiltering::Collapsed: ', isCollapsed, item);
    if (item.isLoading) {
      return;
    }

    const onlyFiltered = !shouldBeLoaded(item) && useFilteredCollapsedIds;
    const needToLoad = item.isCollapsed && !isCollapsed && shouldBeLoaded(item);

    const toUpdate = needToLoad ? [] : [item];
    const toLoad = needToLoad ? [item] : [];

    handleCollapsed(isCollapsed, toUpdate, toLoad, onlyFiltered);
  }

  function handleAllCollapsed(areCollapsed) {
    const toUpdate = collectItems(displayedItems, x =>
      x.isVisible && x.isCollapsed !== areCollapsed && !x.isLoading && !shouldBeLoaded(x)); // Do not touch items, that can have descendants, but not loaded yet

    // console.log('useTreeListFiltering::AllCollapsed: ', areCollapsed, toUpdate, displayedItems);
    handleCollapsed(areCollapsed, toUpdate, [], useFilteredCollapsedIds);
  }

  function handleCollapsed(areCollapsed, toUpdate, toLoad, onlyFiltered) {
    // console.log('useTreeListFiltering::handleCollapsed: ', areCollapsed, toUpdate, toLoad, onlyFiltered);

    const toUpdateIds = toIds(toUpdate);

    if (onlyFiltered) {
      const mergeAction = areCollapsed ? MergeListsAction.Add : MergeListsAction.Remove;
      setFilteredCollapsedIds(mergeLists(filteredCollapsedIds, toUpdateIds, mergeAction));
    }
    else {
      const mergeAction = areCollapsed ? MergeListsAction.Remove : MergeListsAction.Add
      const allExpandedIds = mergeLists(expandedIds, toUpdateIds, mergeAction);
      onItemsCollapsed(areCollapsed, toUpdateIds, toIds(toLoad), allExpandedIds);
    }
  }


  // --- Selection ---
  function handleItemSelected(isSelected, item) {
    handleSelected(isSelected, [item]);
  }

  function handleAllSelected(areSelected) {
    handleSelected(areSelected, collectItems(displayedItems, x => x.isVisible && x.isActive && x.isSelectable && x.isSelected !== areSelected));
  }

  function handleSelected(areSelected, toUpdate) {
    // console.log('useTreeListFiltering::handleSelected: ', areSelected, toUpdate);

    const mergeAction = areSelected ? MergeListsAction.Add : MergeListsAction.Remove;
    const toUpdateIds = toIds(toUpdate);

    let allSelectedIds = mergeLists(selectedIds, toUpdateIds, mergeAction);
    if (allSelectedIds.length > limitSelectionByNumber) {
      allSelectedIds = allSelectedIds.slice(allSelectedIds.length - limitSelectionByNumber);
    }
    onItemsSelected(areSelected, toUpdateIds, allSelectedIds);
  }


  // --- Filtering ---
  function handleFilterChanged(filter) {
    // setFilter(filter);
    useFilteredCollapsedIds && setFilteredCollapsedIds([]);
    onFilterChanged && onFilterChanged(filter);
  }

  function filterItems(items, filter, nestingDepth = 0) {
    let isAnyVisible = false;
    
    const filteredItems = items.map(item => {
      const { items: descendants, isAnyVisible: isAnyDescendantVisible } = !!item.descendants
        ? filterItems(item.descendants, filter, nestingDepth + 1)
        : { items: [], isAnyVisible: false };

      const isActive = !onFilterApplied
        ? includesParttern(item.text, filter)
        : onFilterApplied(item, filter);
      const canHaveDescendants = isAnyDescendantVisible || shouldBeLoaded(item);
      const isVisible = isActive || isAnyDescendantVisible;
      isAnyVisible = isAnyVisible || isVisible;

      return {
        id: item.id,
        text: item.text,
        isLoading: !!item.isLoading,
        isSelected: false,
        isSelectable: item.isSelectable ?? true,
        isCollapsed: !useFilteredCollapsedIds ? true : shouldBeLoaded(item), // If not loaded - stay collapsed, open otherwise,
        isActive,
        isVisible,
        nestingDepth,
        canHaveDescendants,
        descendants,
        originalItem: item, 
      };
    });

    return { isAnyVisible, items: filteredItems };
  }


  // --- Misc ---
  function toId(filteredItem) {
    return filteredItem.id;
  }

  function toIds(filteredItems) {
    return filteredItems.map(x => toId(x));
  }

  function shouldBeLoaded(x) { 
    return x.canHaveDescendants && (!x.descendants || !x.descendants.length); 
  }


  // --- Render ---
  return {
    items: displayedItems,
    areAllCollapsed,
    areAllSelected,
    onItemSelected: handleItemSelected,
    onItemCollapsed: handleItemCollapsed,
    onAllCollapsed: handleAllCollapsed,
    onAllSelected: handleAllSelected,
    onFilterChanged: handleFilterChanged,
  };
}


export function TreeListWithSearch(props)  {
  const {
    onRenderItem,
    onRenderItemTitle,
    showCollapseButton = true,
    searchPlaceholder,
    ...restProps
  } = props;
  const { disabled } = props;

  // console.log('TreeListWithSearch::props :', props);

  const filtering = useTreeListFiltering(props); 
  const { 
    items: filteredItems,
    areAllCollapsed,
    areAllSelected,
    onItemCollapsed,
    onAllSelected,
    onAllCollapsed,
    onFilterChanged,
  } = filtering;

  const handleRenderItemTitle = !!onRenderItemTitle
    ? (item, nestingDepth) => onRenderItemTitle(item, nestingDepth, filtering)
    : undefined;
  
  return (
    <div className={classNames.root}>
      <TreeSearch
        placeholder={searchPlaceholder}
        showCollapseButton={showCollapseButton}
        areAllCollapsed={areAllCollapsed}
        areAllSelected={areAllSelected}
        onAllCollapsed={onAllCollapsed}
        onAllSelected={onAllSelected}
        onFilterChanged={onFilterChanged}
        disabled={disabled}
      />

      <div>
        <TreeList
          {...restProps}
          className={classNames.treeList}
          items={filteredItems}
          showCollapseButtons={showCollapseButton}
          onItemCollapsed={onItemCollapsed}
          onRenderItemTitle={handleRenderItemTitle}
          onRenderItem={onRenderItem}
        />
      </div>
    </div>
  );
}


export function CheckboxTreeListWithSearch(props) {
  const { onRenderItemTitle, disabled } = props;

  function handleRenderItemTitle(item, nestingDepth, filtering) {
    const { text, isSelected, isSelectable, isActive } = item;
    const { onItemSelected } = filtering;
    return (
      <>
      { isSelectable && 
        <Checkbox
          label={!onRenderItemTitle ? text : ''}
          checked={isSelected}
          disabled={!isActive || disabled}
          onChange={(event, selected) => onItemSelected(selected, item) }
        />
      }
      { !!onRenderItemTitle && onRenderItemTitle(item, nestingDepth, filtering)}
      </>
    );
  }

  return <TreeListWithSearch
    { ...props }
    onRenderItemTitle={handleRenderItemTitle} 
  />;  
}


// --- Misc ---
function collectItems(items, criterion) {
  return items.reduce((out, x) => {
    criterion(x) && out.push(x);
    out.push(...collectItems(x.descendants, criterion));
    return out;
  }, []);
}